import { Tooltip } from 'antd';
import Card from 'antd/lib/card';
import Form from 'antd/lib/form';
import { Col, Row } from 'antd/lib/grid';
import Table, { ColumnProps } from 'antd/lib/table';
import AdvancedIdFilter from 'components/AdvancedIdFilter/AdvancedIdFilter';
import CollapsibleCard from 'components/CollapsibleCard/CollapsibleCard';
import { generalColumnWidths, generalLanguageKeys } from 'config/consts';
import {
  MENU_REPORT_DETAIL_ROUTE,
  MENU_REPORT_PREVIEW_ROUTE,
} from 'config/route-consts';
import { crudService, routerService } from 'core/services';
import {
  getOrderTypeForTable,
  renderMasterIndex,
} from 'helpers/ant-design/table';
import { Menu } from 'models/Menu';
import { MenuFilter } from 'models/MenuFilter';
import { MenuReport } from 'models/MenuReport';
import { MenuReportFilter } from 'models/MenuReportFilter';
import { Status } from 'models/Status';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { tableService } from 'services';
import nameof from 'ts-nameof.macro';
import { menuReportRepository } from '../MenuReportRepository';
import { useHistory } from 'react-router';
import { Helmet } from 'react-helmet';

const { Item: FormItem } = Form;

function MenuReportMaster() {
  const [translate] = useTranslation();
  const history = useHistory();

  const [
    filter,
    setFilter,
    list,
    ,
    loading,
    ,
    total,
    ,
    ,
    previewModel,
    ,
    ,
    handleFilter,
    handleSearch,
    handleReset,
    isReset,
    setIsReset,
    handleDefaultSearch,
    ,
    ,
    ,
  ] = crudService.useMaster<MenuReport, MenuReportFilter>(
    MenuReport,
    MenuReportFilter,
    menuReportRepository.count,
    menuReportRepository.list,
    menuReportRepository.get,
  );

  const [, handleGoDetail] = routerService.useMasterNavigation(
    MENU_REPORT_DETAIL_ROUTE,
  );
  const [pagination, sorter, handleTableChange] = tableService.useMasterTable(
    filter,
    setFilter,
    total,
    handleSearch,
  );

  const [menuFilter, setMenuFilter] = React.useState<MenuFilter>(
    new MenuFilter(),
  );
  // Delete handlers -------------------------------------------------------------------------------------------------------------------------------
  const handleGoPreview = React.useCallback(
    (id: number) => {
      history.push(`${MENU_REPORT_PREVIEW_ROUTE}/${id}`);
    },
    [history],
  );
  const columns: ColumnProps<MenuReport>[] = React.useMemo(() => {
    return [
      {
        title: translate(generalLanguageKeys.columns.index),
        key: nameof(generalLanguageKeys.index),
        width: generalColumnWidths.index,
        render: renderMasterIndex<MenuReport>(pagination),
      },

      // {
      //   title: translate('menuReports.site'),
      //   key: nameof(list[0].site),
      //   dataIndex: nameof(list[0].site),
      //   sorter: true,
      //   sortOrder: getOrderTypeForTable<MenuReport>(
      //     nameof(list[0].site),
      //     sorter,
      //   ),
      //   render(site: Site) {
      //     return site?.name;
      //   },
      // },
      {
        title: translate('menuReports.menu'),
        key: nameof(list[0].menu),
        dataIndex: nameof(list[0].menu),
        sorter: true,
        ellipsis: true,
        sortOrder: getOrderTypeForTable<MenuReport>(
          nameof(list[0].menu),
          sorter,
        ),
        render(menu: Menu) {
          return menu?.name;
        },
      },

      {
        title: translate('menuReports.name'),
        key: nameof(list[0].name),
        dataIndex: nameof(list[0].name),
        sorter: true,
        sortOrder: getOrderTypeForTable<MenuReport>(
          nameof(list[0].name),
          sorter,
        ),
      },

      {
        title: translate('menuReports.status'),
        key: nameof(list[0].status),
        dataIndex: nameof(list[0].status),
        sorter: true,
        align: 'center',
        sortOrder: getOrderTypeForTable<MenuReport>(
          nameof(list[0].status),
          sorter,
        ),
        render(status: Status) {
          return (
            <div className={status?.id === 1 ? 'active' : ''}>
              <i className="fa fa-check-circle d-flex justify-content-center"></i>
            </div>
          );
        },
      },

      {
        title: translate(generalLanguageKeys.actions.label),
        key: nameof(generalLanguageKeys.columns.actions),
        dataIndex: nameof(list[0].id),
        width: generalColumnWidths.actions,
        align: 'center',
        render(id: number) {
          return (
            <div className="d-flex justify-content-center button-action-table">
              <Tooltip title={translate('menuReports.tooltip.view')}>
                <button
                  className="btn btn-sm btn-link"
                  onClick={() => handleGoPreview(id)}
                >
                  <i className="tio-visible_outlined" />
                </button>
              </Tooltip>

              <Tooltip title={translate('menuReports.tooltip.edit')}>
                <button
                  className="btn btn-sm btn-link"
                  onClick={handleGoDetail(id)}
                >
                  <i className="tio-edit" />
                </button>
              </Tooltip>
            </div>
          );
        },
      },
    ];
  }, [handleGoDetail, handleGoPreview, list, pagination, sorter, translate]);
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`${translate('documentTitle.menuReport')} - eProcurement Management`}</title>
      </Helmet>
      <div className="page master-page">
        <Card title={translate('menuReports.master.title')}>
          <CollapsibleCard
            className="head-borderless mb-3"
            title={translate(generalLanguageKeys.actions.search)}
          >
            <Form>
              <Row>
                <Col className="pl-1" span={6}>
                  <FormItem
                    className="mb-0"
                    label={translate('menuReports.menu')}
                    labelAlign="left"
                  >
                    <AdvancedIdFilter
                      filter={filter.menuId}
                      filterType={nameof(filter.menuId.equal)}
                      value={filter.menuId.equal}
                      onChange={handleFilter(nameof(filter.menuId))}
                      getList={menuReportRepository.filterListMenu}
                      modelFilter={menuFilter}
                      setModelFilter={setMenuFilter}
                      searchField={nameof(menuFilter.name)}
                      searchType={nameof(menuFilter.name.contain)}
                      placeholder={translate('general.placeholder.title')}
                      isReset={isReset}
                      setIsReset={setIsReset}
                      allowClear={true}
                    />
                  </FormItem>
                </Col>
              </Row>
            </Form>
            <div className="d-flex justify-content-start mt-3 mb-3 btn-filter">
              <>
                <button
                  className="btn btn-sm btn-primary mr-2"
                  onClick={handleDefaultSearch}
                >
                  <i className="tio-filter_outlined mr-2" />
                  {translate(generalLanguageKeys.actions.filter)}
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleReset}
                >
                  <i className="tio-clear_circle_outlined mr-2" />
                  {translate(generalLanguageKeys.actions.reset)}
                </button>
              </>
            </div>
          </CollapsibleCard>
          <Table
            dataSource={list}
            columns={columns}
            size="small"
            tableLayout="fixed"
            loading={loading}
            rowKey={nameof(previewModel.id)}
            pagination={pagination}
            onChange={handleTableChange}
            className="table-none-row-selection"
          />
        </Card>
      </div>
    </>
  );
}

export default MenuReportMaster;
